import React, { useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  FiUsers,
  FiAward,
  FiUserPlus,
  FiClock,
  FiArrowRight,
  FiLock,
  FiTag,
  FiTrendingUp,
  FiZap,
  FiStar,
  FiActivity,
  FiEye,
  FiCheckCircle,
} from "react-icons/fi";
import {
  getRoomStatus,
  formatMemberCount,
  truncateText,
  getFieldImage,
  formatLastActive,
} from "../../../utils/roomHelpers";
import { UserContext } from "../../../context/UserContext";
import "./RoomCard.css";

// ==============================================
// Sub-Components
// ==============================================

// Room Image - Uses photoToUse (signed URL or fallback) from API
const RoomImage = ({ room }) => {
  // Priority: photoToUse > signedProfilePhotoUrl > photoURL > field-based image
  const photoUrl = room.photoToUse || room.signedProfilePhotoUrl || room.photoURL || getFieldImage(room.field);
  
  return (
    <div className="rdcard-image">
      <img
        src={photoUrl}
        alt={room.name}
        onError={(e) => {
          // Fallback chain: try photoURL, then field image, then default
          if (e.target.src !== room.photoURL && room.photoURL) {
            e.target.src = room.photoURL;
          } else if (e.target.src !== getFieldImage(room.field)) {
            e.target.src = getFieldImage(room.field);
          } else {
            e.target.src = "/field-images/default.jpg";
          }
        }}
      />
    </div>
  );
};

// Badge (Trending/Hot/New)
const RoomBadge = ({ badge, room }) => {
  if (!badge) return null;

  const badgeConfig = {
    trending: {
      icon: FiTrendingUp,
      text: `+${room.trendingScore || 0}`,
      className: "rdcard-badge--trending",
    },
    hot: {
      icon: FiZap,
      text: room.lastActivityFormatted || "Active",
      className: "rdcard-badge--hot",
    },
    new: {
      icon: FiClock,
      text: room.daysOld <= 1 ? "Today" : `${room.daysOld || 0}d ago`,
      className: "rdcard-badge--new",
    },
  };

  const config = badgeConfig[badge];
  if (!config) return null;

  const Icon = config.icon;

  return (
    <div className={`rdcard-badge ${config.className}`}>
      <Icon />
      <span>{config.text}</span>
    </div>
  );
};

// Spots Badge
const SpotsBadge = ({ spots }) => {
  if (!spots || spots <= 0) return null;

  return (
    <div className="rdcard-spots">
      {spots} {spots === 1 ? "spot" : "spots"} left
    </div>
  );
};

// Field Tags
const FieldTags = ({ field, subField }) => (
  <div className="rdcard-tags">
    <span className="rdcard-tag rdcard-tag--field">
      <FiTag />
      {field}
    </span>
    {subField && (
      <span className="rdcard-tag rdcard-tag--subfield">{subField}</span>
    )}
  </div>
);

// Scores Display (For You tab)
const ScoresDisplay = ({ scores }) => {
  if (!scores) return null;

  return (
    <div className="rdcard-scores">
      {scores.personalized !== undefined && (
        <div className="rdcard-score rdcard-score--match">
          <FiStar />
          <span>{scores.personalized}% Match</span>
        </div>
      )}
      {scores.quality !== undefined && (
        <div className="rdcard-score rdcard-score--quality">
          <FiActivity />
          <span>{scores.quality} Quality</span>
        </div>
      )}
    </div>
  );
};


// Room Info with See More/Less
const RoomInfo = ({ room }) => {
  const [expanded, setExpanded] = React.useState(false);
  const descriptionLimit = 80;
  const shouldTruncate = room.description && room.description.length > descriptionLimit;
  
  const displayDescription = expanded 
    ? room.description 
    : truncateText(room.description, descriptionLimit);

  const handleToggle = (e) => {
    e.stopPropagation();
    setExpanded(!expanded);
  };

  return (
    <div className="rdcard-info">
      <h3 className="rdcard-title">{truncateText(room.name, 50)}</h3>
      <p className="rdcard-description">
        {displayDescription}
        {shouldTruncate && (
          <button 
            className="rdcard-see-more" 
            onClick={handleToggle}
          >
            {expanded ? " See less" : " See more"}
          </button>
        )}
      </p>
    </div>
  );
};

// Leader & Members Row
const LeaderSection = ({ leader, members, memberCount }) => {
  // Get leader ID to filter them out from member avatars
  const leaderId = leader?._id || leader?.id;
  
  // Filter out duplicates, exclude leader (already shown), and limit to 3
  const displayMembers = useMemo(() => {
    if (!members) return [];
    const seen = new Set();
    return members.filter((member) => {
      const id = member.user?._id || member._id;
      // Skip if it's the leader (they're already shown separately)
      if (leaderId && id === leaderId) return false;
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    }).slice(0, 3);
  }, [members, leaderId]);

  const getInitials = (name) => {
    if (!name) return "?";
    const parts = name.trim().split(" ");
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const getAvatarColor = (name) => {
    const colors = [
      "#3b82f6", "#8b5cf6", "#10b981", "#f59e0b", 
      "#ec4899", "#06b6d4", "#6366f1", "#14b8a6",
    ];
    const index = (name || "").charCodeAt(0) % colors.length;
    return colors[index];
  };

  return (
    <div className="rdcard-leader-section">
      <div className="rdcard-leader">
        {leader?.profilePhotoUrl ? (
          <img
            src={leader.profilePhotoUrl}
            alt={leader.name}
            className="rdcard-leader-avatar"
          />
        ) : (
          <div
            className="rdcard-leader-avatar rdcard-avatar-initials"
            style={{ backgroundColor: getAvatarColor(leader?.name) }}
          >
            {getInitials(leader?.name)}
          </div>
        )}
        <div className="rdcard-leader-info">
          <span className="rdcard-leader-label">
            <FiAward /> Leader
          </span>
          <span className="rdcard-leader-name">
            {leader?.name || "Unknown"}
            {leader?.isVerified && (
              <FiCheckCircle className="rdcard-verified-badge" title="Verified" />
            )}
          </span>
        </div>
      </div>

      <div className="rdcard-members-preview">
        {displayMembers.map((member, idx) => {
          const memberId = member.user?._id || member._id || idx;
          const userName = member.user?.name || "Member";
          const hasPhoto = member.user?.profilePhotoUrl;
          
          return hasPhoto ? (
            <img
              key={`member-${memberId}-${idx}`}
              src={member.user.profilePhotoUrl}
              alt={userName}
              title={userName}
              className="rdcard-member-avatar"
            />
          ) : (
            <div
              key={`member-${memberId}-${idx}`}
              className="rdcard-member-avatar rdcard-avatar-initials"
              style={{ backgroundColor: getAvatarColor(userName) }}
              title={userName}
            >
              {getInitials(userName)}
            </div>
          );
        })}
        {memberCount > 3 && (
          <div className="rdcard-member-avatar rdcard-avatar-more">
            +{memberCount - 3}
          </div>
        )}
      </div>
    </div>
  );
};

// Stats Row
const StatsRow = ({ room, activityTime }) => (
  <div className="rdcard-stats">
    <div className="rdcard-stat">
      <FiUsers />
      <span>{formatMemberCount(room.memberCount || 0, room.maxMembers || 8)}</span>
    </div>
    <div className="rdcard-stat-divider" />
    <div className="rdcard-stat">
      <FiClock />
      <span>{activityTime}</span>
    </div>
  </div>
);

// Action Button
const ActionButton = ({ status, onClick, onPreview, showPreview }) => {
  const buttonConfig = {
    leader: {
      text: "Manage Lab",
      icon: FiArrowRight,
      className: "rdcard-btn--manage",
    },
    member: {
      text: "Enter Lab",
      icon: FiArrowRight,
      className: "rdcard-btn--enter",
    },
    requested: {
      text: "Request Sent",
      icon: FiClock,
      className: "rdcard-btn--pending",
      disabled: true,
    },
    full: {
      text: "Lab Full",
      icon: FiLock,
      className: "rdcard-btn--full",
      disabled: true,
    },
    available: {
      text: "Request to Join",
      icon: FiUserPlus,
      className: "rdcard-btn--join",
    },
  };

  const config = buttonConfig[status] || buttonConfig.available;
  const Icon = config.icon;

  return (
    <div className="rdcard-actions">
      {showPreview && (
        <button className="rdcard-btn rdcard-btn--preview" onClick={onPreview}>
          <FiEye />
          <span>Preview</span>
        </button>
      )}
      <button
        className={`rdcard-btn ${config.className}`}
        onClick={onClick}
        disabled={config.disabled}
      >
        <Icon />
        <span>{config.text}</span>
      </button>
    </div>
  );
};

// Followed Members (Network tab)
const FollowedMembers = ({ followedMembers }) => {
  // Filter out duplicates
  const uniqueMembers = useMemo(() => {
    if (!followedMembers || followedMembers.length === 0) return [];
    const seen = new Set();
    return followedMembers.filter((member) => {
      if (seen.has(member._id)) return false;
      seen.add(member._id);
      return true;
    });
  }, [followedMembers]);

  if (uniqueMembers.length === 0) return null;

  return (
    <div className="rdcard-followed">
      <div className="rdcard-followed-avatars">
        {uniqueMembers.slice(0, 3).map((member, idx) => (
          <img
            key={`followed-${member._id}-${idx}`}
            src={member.profilePhotoUrl}
            alt={member.name}
            className="rdcard-followed-avatar"
          />
        ))}
      </div>
      <span className="rdcard-followed-text">
        {uniqueMembers.length === 1
          ? uniqueMembers[0].name
          : `${uniqueMembers.length} people you follow`}
      </span>
    </div>
  );
};

// ==============================================
// Main Component
// ==============================================
const RoomCard = ({ room, onJoinRequest, showScores = false, badge = null }) => {
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const userId = user?.id || user?._id;

  const status = getRoomStatus(room, userId);

  // Calculate spots available
  const spotsAvailable = useMemo(() => {
    const maxMembers = room.maxMembers || 8;
    const currentMembers = room.memberCount || 0;
    return Math.max(0, maxMembers - currentMembers);
  }, [room.memberCount, room.maxMembers]);

  // Format activity time
  const activityTime = useMemo(() => {
    const lastActive = room.lastActivityAt || room.updatedAt || room.createdAt;
    return lastActive ? formatLastActive(lastActive) : "No activity";
  }, [room.lastActivityAt, room.updatedAt, room.createdAt]);

  // Handle action button click
  const handleAction = (e) => {
    e.stopPropagation();

    if (status === "leader" || status === "member") {
      navigate(`/labs/${room._id}`);
    } else if (status === "available" && onJoinRequest) {
      onJoinRequest(room);
    }
  };

  // Handle preview click
  const handlePreview = (e) => {
    e.stopPropagation();
    navigate(`/labs/${room._id}/preview`);
  };

  // Handle card click
  const handleCardClick = () => {
    if (status === "leader" || status === "member") {
      navigate(`/labs/${room._id}`);
    } else {
      navigate(`/labs/${room._id}/preview`);
    }
  };

  return (
    <div className="rdcard" onClick={handleCardClick}>
      {/* Image Section */}
      <div className="rdcard-image-container">
        <RoomImage room={room} />
        <RoomBadge badge={badge} room={room} />
        {status !== "leader" && status !== "member" && (
          <SpotsBadge spots={spotsAvailable} />
        )}
      </div>

      {/* Content Section */}
      <div className="rdcard-content">
        {/* Tags */}
        <FieldTags field={room.field} subField={room.subField} />

        {/* Room Info */}
        <RoomInfo room={room} />

        {/* Scores (if showing) */}
        {showScores && room.scores && <ScoresDisplay scores={room.scores} />}

        {/* Followed Members (Network) */}
        {room.followedMembers && <FollowedMembers followedMembers={room.followedMembers} />}

        {/* Leader & Members */}
        <LeaderSection
          leader={room.leader}
          members={room.members}
          memberCount={room.memberCount}
        />

        {/* Stats */}
        <StatsRow room={room} activityTime={activityTime} />

        {/* Action Buttons */}
        <ActionButton
          status={status}
          onClick={handleAction}
          onPreview={handlePreview}
          showPreview={true}
        />
      </div>
    </div>
  );
};

export default RoomCard;
